import { useCallback, useEffect, useRef, useState } from 'react';

import type { FacebookGroupRecommendation } from '#src/app/pages/psf/shared/api';

import type { FacebookPostDestination, FacebookPostSheetGroupDestination } from './types';

/**
 * Runs `onChange` while rendering, on the open or the close itself, rather than in an effect, so an
 * open never shows a frame of the last open's state: https://react.dev/learn/you-might-not-need-an-effect
 */
function useOnOpenChange(isOpen: boolean, onChange: (open: boolean) => void) {
	const [wasOpen, setWasOpen] = useState(isOpen);
	if (wasOpen !== isOpen) {
		setWasOpen(isOpen);
		onChange(isOpen);
	}
}

export type Step = 'destination' | 'feed' | 'groups';

/**
 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment.
 * The `variant_2` steps: the destination choice, then the group cards, and the group the supporter
 * picked. Continue with the feed picked calls `onPost`, which opens the composer, so the feed choice has
 * no step of its own. Each open starts from the first step with nothing picked, and reads the first step
 * from the `groupDestination` of that open, not of the mount. With no `groupDestination`, the sheet
 * stays on the `feed` step, which is the `variant_1` flow.
 */
export function useSheetSteps(
	isOpen: boolean,
	groupDestination: FacebookPostSheetGroupDestination | undefined,
	onPost: () => void,
) {
	const firstStep: Step = groupDestination ? 'destination' : 'feed';
	const [step, setStep] = useState<Step>(firstStep);
	const [destination, setDestination] = useState<FacebookPostDestination>('feed');
	const [selectedGroup, setSelectedGroup] = useState<FacebookGroupRecommendation | null>(null);

	useOnOpenChange(isOpen, (open) => {
		if (!open) return;
		setStep(firstStep);
		setDestination('feed');
		setSelectedGroup(null);
	});

	const continueToDestination = useCallback(() => {
		if (destination === 'group') {
			setStep('groups');
			return;
		}
		onPost();
	}, [destination, onPost]);

	const selectGroup = useCallback(
		(group: FacebookGroupRecommendation) => {
			setSelectedGroup(group);
			groupDestination?.onSelectGroup(group);
		},
		[groupDestination],
	);

	return {
		step,
		destination,
		setDestination,
		continueToDestination,
		selectedGroup,
		selectGroup,
	};
}

/** One view per open: a re-render while open, a move between steps, or a new `onView` identity, fires nothing. */
export function useViewedOnce(isOpen: boolean, onView: () => void) {
	const viewedRef = useRef(false);
	useEffect(() => {
		if (!isOpen) {
			viewedRef.current = false;
			return;
		}
		if (viewedRef.current) return;
		viewedRef.current = true;
		onView();
	}, [isOpen, onView]);
}
